// MonthlyAnalysic.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
} from "@mui/material";
import api from "../../api/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const formatNumber = (num) => (num === null || num === undefined ? "0" : num.toLocaleString());

const MonthlyAnalysic = () => {
  const [selectedMonth, setSelectedMonth] = useState("January");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [financialTotals, setFinancialTotals] = useState(null);
  const pdfChartRef = useRef();

  useEffect(() => {
    fetchMonthlyData(selectedMonth);
  }, [selectedMonth]);

  const fetchMonthlyData = async (month) => {
    setLoading(true);
    try {
      const response = await api.get(`fees/by-month/?month=${month}`);
      const fees = response.data.fees || [];
      const expenditures = response.data.expenditures || [];

      const aggregation = {};
      fees.forEach(f => {
        const key = f.fee_type_label || f.fee_type;
        aggregation[key] = (aggregation[key] || 0) + f.amount;
      });
      expenditures.forEach(e => {
        const key = e.category_label || e.category;
        aggregation[key] = (aggregation[key] || 0) + e.amount;
      });

      const chartData = Object.entries(aggregation).map(([name, amount]) => ({
        name,
        amount: parseFloat(amount.toFixed(2)),
      }));

      setData(chartData);
    } catch (error) {
      console.error("Error fetching monthly data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialTotals = async () => {
    try {
      const response = await api.get("financial-totals/");
      setFinancialTotals(response.data);
      generatePDF(response.data);
    } catch (error) {
      console.error("Error fetching financial totals:", error);
    }
  };

  const generatePDF = async (totals) => {
    if (!pdfChartRef.current) return;

    const canvas = await html2canvas(pdfChartRef.current, { scale: 2, backgroundColor: "#f5f5f5" });
    const imgData = canvas.toDataURL("image/png");

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    let yOffset = 15;

    // Background color
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageWidth, 297, "F");

    // Title
    doc.setFontSize(18);
    doc.setTextColor(33, 37, 41);
    doc.text("Overall Financial Totals", pageWidth / 2, yOffset, { align: "center" });
    yOffset += 10;

    // Totals display (flex-like boxes)
    const boxWidth = pageWidth - 60;
    const boxHeight = 12;
    const spacing = 6;

    const boxes = [
      { label: "Overall Fee Total", value: totals.overall_fee_total, fill: "#bbdefb" },
      { label: "Overall Expenditure Total", value: totals.overall_expenditure_total, fill: "#ffcdd2" },
      { label: "Net Total", value: totals.net_total, fill: "#e0e0e0" },
    ];

    boxes.forEach((b) => {
      // Shadow effect
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(230, 230, 230);
      doc.roundedRect(30, yOffset - 9, boxWidth, boxHeight, 2, 2, "F");

      // Colored bar
      doc.setFillColor(...hexToRgb(b.fill));
      doc.rect(30, yOffset - 9, boxWidth, boxHeight, "F");

      // Text
      doc.setFontSize(11);
      doc.setTextColor(33, 37, 41);
      doc.text(`${b.label}: $${formatNumber(b.value)}`, pageWidth / 2, yOffset, { align: "center" });

      yOffset += boxHeight + spacing;
    });

    yOffset += 10;

    // Chart
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = pageWidth - 40;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    doc.addImage(imgData, "PNG", 20, yOffset, pdfWidth, pdfHeight);

    // Footer with date
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Report generated on: ${dateStr}`, pageWidth / 2, 285, { align: "center" });

    doc.save(`Overall_Financial_Totals.pdf`);
  };

  const hexToRgb = (hex) => {
    const c = hex.replace("#", "");
    return [parseInt(c.substr(0, 2), 16), parseInt(c.substr(2, 2), 16), parseInt(c.substr(4, 2), 16)];
  };

  const pdfChartData = financialTotals
    ? [
        { name: "Overall Fee Total", amount: financialTotals.overall_fee_total, fill: "#bbdefb" },
        { name: "Overall Expenditure Total", amount: financialTotals.overall_expenditure_total, fill: "#ffcdd2" },
        { name: "Net Total", amount: financialTotals.net_total, fill: "#e0e0e0" },
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Monthly Financial Analysis
      </Typography>

      <FormControl sx={{ mb: 2, minWidth: 200 }}>
        <InputLabel>Select Month</InputLabel>
        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          {MONTHS.map((month) => <MenuItem key={month} value={month}>{month}</MenuItem>)}
        </Select>
      </FormControl>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `$${formatNumber(value)}`} />
              <Area type="monotone" dataKey="amount" stroke="#8884d8" fill="#bbdefb" />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Box sx={{ mt: 3 }}>
       <Button
  variant="contained"
  onClick={fetchFinancialTotals}
  sx={{
    background: 'linear-gradient(135deg, #4A90E2, #50E3C2)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: '10px 25px',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #50E3C2, #4A90E2)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
  }}
>
  Download OAFR
</Button>

      </Box>

      {/* Hidden PDF chart for capture */}
      <Box sx={{ position: "absolute", left: "-9999px", width: 800, height: 400 }} ref={pdfChartRef}>
        {financialTotals && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pdfChartData} margin={{ top: 40, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `$${formatNumber(value)}`} />
              <Bar dataKey="amount">
                {pdfChartData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default MonthlyAnalysic;
